import { useState } from 'react'
import { s } from '@w/schema'
import { ulid } from '@w/utils'
import { fold } from '@w/db-custom/names'
import { Button } from '@w/ui/components/button'
import { FileSpreadsheetIcon, LayoutIcon } from '@w/ui/components/icon'
import { Input } from '@w/ui/components/input'
import { Dialog, DialogPopup, DialogTitle } from '@w/ui/components/dialog'
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from '@w/ui/components/select'
import { Menu, MenuTrigger, MenuPopup, MenuItem } from '@w/ui/components/menu'
import type { DatabaseSort, DatabaseFilter } from './database-sql'

export type GridDisplay = { search: string; sort?: DatabaseSort; filter?: DatabaseFilter; view: 'table' | 'board' }
export type DatabaseViews = { activeId: string; views: { id: string; name: string; display: GridDisplay }[] }
type ViewStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const direction = s.enum(['asc', 'desc'])
const storedViewsSchema = s.object({ activeId: s.string(), views: s.array(s.object({ id: s.string(), name: s.string(), display: s.object({
  view: s.enum(['table', 'board']),
  sort: s.union([s.object({ column: s.string(), direction }), s.object({ pageField: s.literal('editedAt'), direction })]).optional(),
  filter: s.object({ column: s.string(), optionId: s.string().optional() }).optional(),
}) })) })

export function databaseViewsKey(userId: string, workspaceId: string, table: string) { return `database-views:${userId}:${workspaceId}:${fold(table)}` }

export function loadDatabaseViews(storage: ViewStorage, key: string): DatabaseViews {
  const fallback: DatabaseViews = { activeId: 'table', views: [{ id: 'table', name: 'Table', display: { view: 'table', search: '' } }] }
  try {
    // Blocked storage (Safari private mode, disabled site data) throws on read: open the default view.
    const json = storage.getItem(key)
    if (!json) return fallback
    const parsed = storedViewsSchema.parse(JSON.parse(json))
    if (parsed.views.length === 0 || new Set(parsed.views.map(view => view.id)).size !== parsed.views.length || !parsed.views.some(view => view.id === parsed.activeId) || parsed.views.some(view => !view.id || !view.name.trim())) return fallback
    return { ...parsed, views: parsed.views.map(view => ({ id: view.id, name: view.name, display: { ...view.display, search: '' } })) }
  } catch { return fallback }
}

export function saveDatabaseViews(storage: ViewStorage, key: string, state: DatabaseViews) {
  storage.setItem(key, JSON.stringify({ ...state, views: state.views.map(view => {
    const { search: _search, ...display } = view.display
    return { ...view, display }
  }) }))
}

/** Stage device preferences before SQL; failed SQL restores only keys this operation changed. */
export async function changeDatabaseViewPreferences(storage: ViewStorage, oldKey: string, next: { key: string; state: DatabaseViews } | undefined, change: () => Promise<void>) {
  const changed: { key: string; value: string | null }[] = []
  try {
    if (next) {
      const value = storage.getItem(next.key)
      saveDatabaseViews(storage, next.key, next.state)
      changed.push({ key: next.key, value })
    }
    if (oldKey !== next?.key) {
      const value = storage.getItem(oldKey)
      storage.removeItem(oldKey)
      changed.push({ key: oldKey, value })
    }
    await change()
  } catch (error) {
    try {
      for (const entry of changed.toReversed()) {
        if (entry.value === null) storage.removeItem(entry.key)
        else storage.setItem(entry.key, entry.value)
      }
    } catch (restoreError) { throw new SuppressedError(restoreError, error, 'Could not restore database view preferences') }
    throw error
  }
}

export function NamedDatabaseViews({ state, onChange, error }: { state: DatabaseViews; onChange: (next: DatabaseViews) => boolean; error: string | null }) {
  const [dialog, setDialog] = useState<'create' | 'rename' | null>(null)
  const [name, setName] = useState('')
  const [layout, setLayout] = useState<'table' | 'board'>('table')
  const active = state.views.find(view => view.id === state.activeId) ?? state.views[0]
  function submit() {
    if (!name.trim()) return
    if (dialog === 'rename') {
      if (onChange({ ...state, views: state.views.map(view => view.id === active.id ? { ...view, name: name.trim() } : view) })) setDialog(null)
    } else {
      const id = ulid()
      if (onChange({ activeId: id, views: [...state.views, { id, name: name.trim(), display: { view: layout, search: '' } }] })) setDialog(null)
    }
  }
  return <div className="flex flex-col gap-2">
    <div className="flex flex-wrap items-center gap-2" aria-label="Database views on this device">
      {state.views.map(view => view.id === active.id ? <Menu key={view.id}><MenuTrigger data-testid={`database-named-view-${view.name}`} render={<Button size="sm" variant="outlined" aria-current="true" title="Views saved on this device" />}>{view.display.view === 'table' ? <FileSpreadsheetIcon /> : <LayoutIcon />}<span data-testid="database-view-active">{view.name}</span></MenuTrigger><MenuPopup>
        <MenuItem data-testid="database-view-rename" onClick={() => { setName(active.name); setDialog('rename') }}>Rename</MenuItem>
        <MenuItem data-testid="database-view-duplicate" onClick={() => { const id = ulid(); onChange({ activeId: id, views: [...state.views, { ...active, id, name: `${active.name} (copy)`, display: { ...active.display, search: '' } }] }) }}>Duplicate</MenuItem>
        {state.views.length > 1 ? <MenuItem data-testid="database-view-delete" onClick={() => { const views = state.views.filter(view => view.id !== active.id); onChange({ views, activeId: views[0].id }) }}>Delete</MenuItem> : null}
      </MenuPopup></Menu> : <Button size="sm" key={view.id} variant="text" data-testid={`database-named-view-${view.name}`} onClick={() => { onChange({ ...state, activeId: view.id }) }}>{view.display.view === 'table' ? <FileSpreadsheetIcon /> : <LayoutIcon />}{view.name}</Button>)}
      <Button size="sm" variant="text" aria-label="New view" data-testid="database-new-view" onClick={() => { setName(''); setLayout('table'); setDialog('create') }}>+</Button>
      <span className="sr-only">Views saved on this device</span>
    </div>
    {error ? <p role="alert">{error}</p> : null}
    <Dialog open={dialog !== null} onOpenChange={open => { if (!open) setDialog(null) }}><DialogPopup className="flex flex-col gap-4 p-6"><DialogTitle>{dialog === 'rename' ? 'Rename view' : 'New view'}</DialogTitle>
      {dialog === 'create' ? <Select value={layout} onValueChange={value => { if (value) setLayout(value) }}><SelectTrigger aria-label="View layout" data-testid="database-view-layout"><SelectValue /></SelectTrigger><SelectPopup><SelectItem value="table" data-testid="database-view-layout-table">Table</SelectItem><SelectItem value="board" data-testid="database-view-layout-board">Board</SelectItem></SelectPopup></Select> : null}
      <Input aria-label="View name" placeholder="View name" data-testid="database-view-name" value={name} onChange={event => { setName(event.target.value) }} onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); submit() } }} />
      {error ? <p role="alert">{error}</p> : null}
      <div className="flex gap-2"><Button variant="text" onClick={() => { setDialog(null) }}>Cancel</Button><Button data-testid={dialog === 'rename' ? 'database-view-save' : 'database-view-create'} disabled={!name.trim()} onClick={submit}>{dialog === 'rename' ? 'Save' : 'Create'}</Button></div>
    </DialogPopup></Dialog>
  </div>
}
